import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useCallback, useMemo } from "react";
import { ALPHA, INK, INK_BLACK, STATUS, SURFACE } from "../../../theme/tokens";
import { type SelfScheduleCalendarDayData, SelfSchedulingPreference } from "../../../utils/models";

// Functional: PTO indicator dot color. Stable across themes so the chip stays readable.
const PTO_BG = "#4e4b45cc";

import { SubIcon } from "./SubIcon";
import { SubText } from "./SubText";

type SelfSchedulingDayProps = { dayData: SelfScheduleCalendarDayData; onClick: (date: DateTime) => void; isSelected?: boolean };

export const SelfSchedulingDay: FC<SelfSchedulingDayProps> = ({ dayData, onClick, isSelected }) => {
	const colors = useMemo<{ bg: string; text: string }>(() => {
		switch (dayData.dayPreference) {
			case SelfSchedulingPreference.PTO:
				return { bg: PTO_BG, text: SURFACE };
			case SelfSchedulingPreference.WANT_TO_WORK:
				return { bg: alpha(STATUS.SUCCESS.main, 0.8), text: SURFACE };
			default:
				return { bg: SURFACE, text: INK.SECONDARY };
		}
	}, [dayData.dayPreference]);

	const handleClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>): void => {
			event.stopPropagation();
			event.preventDefault();
			onClick(dayData.date);
		},
		[dayData.date, onClick],
	);

	return (
		<Box
			component="button"
			data-testid={`self-scheduling-day-${dayData.date.toFormat("yyyy-MM-dd")}`}
			onClick={handleClick}
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				cursor: "pointer",
				gap: "1px",
				border: isSelected ? `2px solid ${STATUS.INFO.main}` : "2px solid transparent",
				borderRadius: "8px",
				boxSizing: "border-box",
				bgcolor: "transparent",
				padding: "4px",
				outline: "none",
				touchAction: "manipulation",
				"&:hover": { bgcolor: alpha(INK_BLACK, ALPHA.HOVER_OVERLAY) },
			}}
		>
			<Box
				sx={{
					width: "30px",
					height: "30px",
					bgcolor: colors.bg,
					borderRadius: "50%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				<Typography sx={{ fontWeight: 600, fontSize: "14px", color: colors.text, userSelect: "none" }}>{dayData.date.day}</Typography>
			</Box>
			<SubText dayData={dayData} />
			<SubIcon dayData={dayData} />
		</Box>
	);
};
